import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

import { useMutation, useApolloClient, useLazyQuery } from "react-apollo";

import Section from "../Section";
import { EditFormButtonGroup } from "../inputs";

import {
  UPDATE_ORDER_DETAIL_MUTATION,
  DELETE_ORDER_DETAIL_MUTATION
} from "../../graphql/Orders/Orders.graphql";

import { ourToast as toast } from "../../utils";

import { SINGLE_SYSTEM_CODE_QUERY } from "../../graphql/SystemCodes/SystemCodes.graphql";

import { SINGLE_PRODUCT_QUERY } from "../../graphql/Products/Products.graphql";

import OrderDetailBillingForm from "./OrderDetailBillingForm";
import OrderDetailDispatcherForm from "./OrderDetailDispatcherForm";

const DEFAULT_STATE = {
  ProductRecIdRef: "",
  Description: "",
  Quantity: "",
  Hazmat: "",
  UOMRecIdRef: "",
  InternalComm: "",
  ExternalComm: "",
  Rate: "",
  Note: "",
  Percentage: "",
  ProdCatgRecIdRef: "",
  OverrideFlag: false
};

const EditOrderDetailForm = ({ orderDetail, setModalOpen, isDispatcher }) => {
  const client = useApolloClient();

  const [updateOrderDetail, { loading }] = useMutation(
    UPDATE_ORDER_DETAIL_MUTATION,
    {
      refetchQueries: ["OrderDetails"]
    }
  );

  const [state, setState] = useState({ ...DEFAULT_STATE });

  const [getSystemCode, { data: systemCodeData }] = useLazyQuery(
    SINGLE_SYSTEM_CODE_QUERY
  );

  useEffect(() => {
    const UOMRecIdRef = parseInt(state.UOMRecIdRef, 10);
    if (UOMRecIdRef) {
      getSystemCode({
        variables: {
          where: {
            RecId: UOMRecIdRef
          }
        }
      });
    }
  }, [getSystemCode, state.UOMRecIdRef]);

  useEffect(() => {
    if (orderDetail) {
      setState(prev => ({ ...prev, ...orderDetail }));
    }
  }, [orderDetail]);

  const fetchProductData = useCallback(
    async productRecIdRef => {
      try {
        const { data: ProductData } = await client.query({
          query: SINGLE_PRODUCT_QUERY,
          variables: {
            where: {
              RecId: parseInt(productRecIdRef, 10)
            }
          }
        });
        if (ProductData && ProductData.Product) {
          setState(prev => ({
            ...prev,
            Rate: ProductData.Product.Price
              ? ProductData.Product.Price
              : prev.Rate,
            Hazmat: ProductData.Product.HazmatCode,
            ProdCatgRecIdRef: ProductData.Product.ProductCatgRecIdRef,
            Percentage: ProductData.Product.Percentage,
            UOMRecIdRef: ProductData.Product.UOMRecIdRef,
            Description: ProductData.Product.Description
          }));
        }
      } catch (err) {
        toast.error("Error getting Product.");
      }
    },
    [client]
  );
  const handleChange = (name, value) => {
    console.log({ name, value });
    setState(prev => ({ ...prev, [name]: value }));
    if (name === "ProductRecIdRef") {
      fetchProductData(value);
    }
  };

  const UOMCodeDescription = systemCodeData?.SystemCode?.CodeDescription;

  useEffect(() => {
    if (UOMCodeDescription === "Flat Fee") {
      setState(prev => ({ ...prev, Quantity: 1 }));
    }
  }, [UOMCodeDescription]);

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      await updateOrderDetail({
        variables: {
          data: {
            OrderRecIdRef: parseInt(orderDetail.OrderRecIdRef),
            ProductRecIdRef: parseInt(state.ProductRecIdRef),
            Description: state.Description,
            Quantity: parseFloat(state.Quantity),
            Hazmat: state.Hazmat,
            UOMRecIdRef: parseInt(state.UOMRecIdRef),
            InternalComm: state.InternalComm,
            ExternalComm: state.ExternalComm,
            Rate: parseFloat(state.Rate),
            Percentage: parseFloat(state.Percentage),
            Note: state.Note,
            ProdCatgRecIdRef: parseInt(state.ProdCatgRecIdRef),
            OverrideFlag: Boolean(state.OverrideFlag)
          },
          where: {
            RecId: orderDetail.RecId
          }
        }
      });
      setModalOpen(false, "");
      toast.success("Order Detail updated successfully.");
    } catch (err) {
      toast.error("Error updating Order Detail.");
    }
  };

  return (
    <form id="edit-order-detail" method="POST" onSubmit={handleSubmit}>
      <fieldset aria-busy={loading}>
        <Section title="Edit Order Detail">
          {isDispatcher ? (
            <OrderDetailDispatcherForm inputs={state} onChange={handleChange} />
          ) : (
            <OrderDetailBillingForm inputs={state} onChange={handleChange} />
          )}
          <EditFormButtonGroup
            deleteMutation={DELETE_ORDER_DETAIL_MUTATION}
            form="edit-order-detail"
            refetchQueries={["OrderDetails"]}
            variables={{ where: { RecId: orderDetail.RecId } }}
            onComplete={() => setModalOpen(false, "")}
          />
        </Section>
      </fieldset>
    </form>
  );
};

EditOrderDetailForm.propTypes = {
  orderDetail: PropTypes.object,
  isDispatcher: PropTypes.bool,
  setModalOpen: PropTypes.func
};

export default EditOrderDetailForm;
